import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Sun, Sunset, Moon } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import Icon from '../../../components/AppIcon';
import { useMySubscription } from '../../../hooks/useMySubscription';
import { useToast, ToastRenderer } from '../../../hooks/useToast';
import { notifyHrToUpgrade } from '../../../api/General/Subscription';
import { Roles } from '../../../models/general';
import { BROCHURE_MODULES } from '../../../data/brochureModules';
import { cn } from '../../../utils/cn';
import { track, AnalyticsEvents } from '../../../utils/analytics';

type Props = Readonly<{
  firstName?: string;
}>;

const FreeTierShowcase: React.FC<Props> = ({ firstName }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data } = useMySubscription();
  const role = useSelector((s: { user?: { role?: string } }) => s.user?.role);
  const { toast, showToast, dismissToast } = useToast();
  const isRtl = i18n.dir() === 'rtl';

  const [nudgePending, setNudgePending] = useState(false);

  const { greetingText, GreetingIcon } = useMemo(() => {
    const hour = new Date().getHours();
    if (hour >= 5 && hour < 12) return { greetingText: t('Good morning'), GreetingIcon: Sun };
    if (hour >= 12 && hour < 18) return { greetingText: t('Good afternoon'), GreetingIcon: Sun };
    if (hour >= 18 && hour < 22) return { greetingText: t('Good evening'), GreetingIcon: Sunset };
    return { greetingText: t('Good night'), GreetingIcon: Moon };
  }, [t]);

  const greetingTitle = `${greetingText}${firstName ? `, ${firstName}` : ''}`;

  const callerIsHrOrAdmin = role === Roles.Hr || role === 'Hr' || role === Roles.Admin || role === 'Admin';
  const canStartTrial = !!data?.canStartTrial;

  const handleStartTrial = useCallback(() => {
    track(AnalyticsEvents.StartTrialCtaClicked, { source: 'dashboard-showcase' });
    if (canStartTrial || callerIsHrOrAdmin) {
      navigate('/start-trial');
    } else {
      showToast(t('Please ask your HR or admin to start the trial.'), 'info');
    }
  }, [canStartTrial, callerIsHrOrAdmin, navigate, showToast, t]);

  const handleNudgeHr = useCallback(async () => {
    if (nudgePending) return;
    setNudgePending(true);
    try {
      const res = await notifyHrToUpgrade();
      if (res.errors && res.errors.length > 0) {
        showToast(res.errors[0]?.description || t("Couldn't notify your HR — please contact support."), 'error');
      } else {
        track(AnalyticsEvents.NudgeHrSubmitted, { source: 'dashboard-showcase' });
        showToast(t('Your HR has been notified.'), 'success');
      }
    } catch {
      showToast(t("Couldn't notify your HR — please contact support."), 'error');
    } finally {
      setNudgePending(false);
    }
  }, [nudgePending, showToast, t]);

  const handleExploreModule = useCallback(
    (slug: string) => {
      navigate(`/brochure/${slug}`);
    },
    [navigate],
  );

  const showStartTrialCta = canStartTrial || callerIsHrOrAdmin;

  return (
    <PageLayout title={greetingTitle} icon={GreetingIcon} accentColor="blue" maxWidth="1600px">
      <ToastRenderer toast={toast} onDismiss={dismissToast} />

      {/* Hero block — calm, single-column intro framing the showcase */}
      <section
        aria-labelledby="free-showcase-heading"
        className="relative overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-indigo-50 via-white to-amber-50/40 p-8 md:p-10 mb-8"
      >
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-indigo-700">
          <Icon name="Sparkles" size={14} aria-hidden="true" />
          <span>{t('Mowazaf for everyone')}</span>
        </div>
        <h2
          id="free-showcase-heading"
          className="mt-3 max-w-3xl text-2xl md:text-3xl font-semibold leading-tight text-slate-900"
        >
          {t('A complete HR workspace — explore everything Mowazaf can do for your team.')}
        </h2>
        <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-slate-600">
          {t("You're on the free plan. Take a tour of the modules below — start a trial whenever you're ready to switch them on.")}
        </p>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          {showStartTrialCta ? (
            <button
              type="button"
              onClick={handleStartTrial}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-5 py-2.5',
                'bg-indigo-600 text-sm font-semibold text-white shadow-sm',
                'hover:bg-indigo-700 hover:shadow',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                'transition-all',
              )}
            >
              <Icon name="Rocket" size={16} aria-hidden="true" />
              <span>{t('Start free trial')}</span>
              <Icon name="ArrowRight" size={14} className={isRtl ? 'rotate-180' : ''} aria-hidden="true" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNudgeHr}
              disabled={nudgePending}
              className={cn(
                'inline-flex items-center gap-2 rounded-lg px-5 py-2.5',
                'bg-indigo-600 text-sm font-semibold text-white shadow-sm',
                'hover:bg-indigo-700 hover:shadow',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                'transition-all disabled:opacity-60 disabled:cursor-not-allowed',
              )}
            >
              <Icon name="MailPlus" size={16} aria-hidden="true" />
              <span>{nudgePending ? t('Notifying…') : t('Ask your HR to upgrade')}</span>
            </button>
          )}
          <a
            href="https://mowazaf.com/pricing/"
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg px-4 py-2.5',
              'text-sm font-medium text-slate-700',
              'hover:bg-slate-100 hover:text-slate-900',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300 focus-visible:ring-offset-2',
              'transition-all',
            )}
          >
            {t('See pricing')}
            <Icon name="ArrowUpRight" size={14} aria-hidden="true" />
          </a>
        </div>
      </section>

      {/* Module grid — same data + visual rhythm as the BrochureOverlay */}
      <section aria-labelledby="free-showcase-modules">
        <div className="mb-4 flex items-center gap-3">
          <h3
            id="free-showcase-modules"
            className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]"
          >
            {t('Every module, included')}
          </h3>
          <div className="flex-1 h-px bg-border/60" />
          <span className="text-xs text-muted-foreground">
            {t('{{count}} modules', { count: BROCHURE_MODULES.length })}
          </span>
        </div>

        <ul className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {BROCHURE_MODULES.map((mod) => (
            <li key={mod.slug}>
              <button
                type="button"
                onClick={() => handleExploreModule(mod.slug)}
                className={cn(
                  'group flex h-full w-full flex-col items-start gap-3 rounded-xl text-start',
                  'border border-slate-200 bg-white p-5',
                  'shadow-[0_1px_2px_rgba(15,23,42,0.04)]',
                  'transition-all duration-200',
                  'hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)] hover:border-slate-300',
                  'ring-2 ring-transparent',
                  mod.accent.ring,
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2',
                )}
              >
                <span
                  className={cn(
                    'inline-flex h-10 w-10 items-center justify-center rounded-lg',
                    mod.accent.surface,
                  )}
                >
                  <Icon name={mod.iconName} size={20} className={mod.accent.iconText} aria-hidden="true" />
                </span>
                <div className="space-y-1">
                  <p className="text-base font-semibold text-slate-900">{t(mod.nameKey)}</p>
                  <p className="text-sm leading-relaxed text-slate-500">{t(mod.pitchKey)}</p>
                </div>
                <span className="mt-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-500 group-hover:text-slate-900 transition-colors">
                  {t('Explore')}
                  <Icon
                    name="ArrowRight"
                    size={12}
                    className={cn(
                      'transition-transform group-hover:translate-x-0.5',
                      isRtl ? 'rotate-180 group-hover:-translate-x-0.5 group-hover:translate-x-0' : '',
                    )}
                    aria-hidden="true"
                  />
                </span>
              </button>
            </li>
          ))}
        </ul>
      </section>

      {/* Quiet footer — same supporting links as the brochure overlay */}
      <footer className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 pt-6 text-xs text-slate-500">
        <p>{t("Questions about what's included? We're happy to walk you through it.")}</p>
        <div className="flex items-center gap-4">
          <a
            href="https://wa.me/971547888992"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-slate-700 hover:text-indigo-600"
          >
            {t('Chat on WhatsApp')}
          </a>
          <span aria-hidden="true" className="text-slate-300">·</span>
          <a
            href="https://mowazaf.com/contact-us/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-slate-700 hover:text-indigo-600"
          >
            {t('Contact sales')}
          </a>
        </div>
      </footer>
    </PageLayout>
  );
};

export default FreeTierShowcase;
